<template>
  <div class="col-sm-12 trust-report-page">
    <div class="trust-report-container">
      <!-- Filters -->
      <div class="filter-panel">
        <div class="filter-container">
          <!-- Distributor -->
          <div class="filter-item distributor-filter">
            <label>Distributor</label>
            <select
              v-model="selectedDistributorId"
              @change="onDistributorChange"
              :disabled="isDistributorLocked || loading"
            >
              <option value="" disabled>Select Distributor</option>

              <option v-if="!isDistributorUser" :value="ALL">
                All Distributors
              </option>

              <option
                v-for="distributor in distributorList"
                :key="distributor.profile_id"
                :value="String(distributor.profile_id)"
              >
                {{
                  distributor.business_name ||
                  distributor.business_display_name ||
                  distributor.name ||
                  "Distributor #" + distributor.profile_id
                }}
              </option>
            </select>
          </div>

          <!-- From Date -->
          <div class="filter-item date-filter">
            <label>From Date</label>
            <input
              type="date"
              v-model="fromDate"
              :max="currentDate"
              @change="handleDateChange"
              @blur="validateFromDate"
              :disabled="loading"
            />
          </div>

          <!-- To Date -->
          <div class="filter-item date-filter">
            <label>To Date</label>
            <input
              type="date"
              v-model="toDate"
              :max="currentDate"
              @change="handleDateChange"
              @blur="validateToDate"
              :disabled="loading"
            />
          </div>

          <!-- Dealer Category -->
          <div class="filter-item category-filter">
            <label>Dealer Category</label>
            <select
              v-model="selectedCategoryType"
              @change="onCategoryTypeChange"
              :disabled="!selectedDistributorId || loading"
            >
              <option :value="null" disabled>Select Category</option>
              <option value="individual">Individual</option>
              <option value="group">Group</option>
            </select>
          </div>

          <!-- Group Branch -->
          <div
            v-if="selectedCategoryType === 'group' && selectedDistributorId"
            class="filter-item group-filter"
          >
            <label>Dealer Group</label>
            <select
              v-model="selectedDealerGroup"
              @change="onDealerGroupChange"
              :disabled="loadingDealerGroups || loading"
            >
              <option :value="null" disabled>
                {{ loadingDealerGroups ? "Loading..." : "Select Group" }}
              </option>

              <option
                v-for="group in filteredDealerGroups"
                :key="group.group_id"
                :value="group"
              >
                {{ group.group_name }}
              </option>
            </select>
          </div>

          <!-- Individual Branch: Dealer Type -->
          <div
            v-if="
              selectedCategoryType === 'individual' && selectedDistributorId
            "
            class="filter-item type-filter"
          >
            <label>Dealer Type</label>
            <select
              v-model="selectedBusinessType"
              @change="onBusinessTypeChange"
              :disabled="loadingDealerTypes || loading"
            >
              <option value="" disabled>
                {{
                  loadingDealerTypes
                    ? "Loading Dealer Types..."
                    : "Select Dealer Type"
                }}
              </option>

              <option
                v-for="category in dealerCategoryList"
                :key="category.id"
                :value="String(category.id)"
              >
                {{ category.dealer_cat_name }}
              </option>
            </select>
          </div>

          <!-- Dealer -->
          <div v-if="showDealerDropdown" class="filter-item dealer-filter">
            <label>Dealer</label>
            <select v-model="selectedDealerId" :disabled="loading">
              <option value="" disabled>Select Dealer</option>

              <option v-if="dealerList.length" :value="ALL">
                All Dealers
              </option>

              <option
                v-for="dealer in dealerList"
                :key="dealer.profile_id"
                :value="String(dealer.profile_id)"
              >
                {{
                  dealer.business_display_name ||
                  dealer.business_name ||
                  dealer.name ||
                  "Dealer #" + dealer.profile_id
                }}
              </option>
            </select>
          </div>

          <!-- Search -->
          <div class="filter-item button-filter">
            <label style="visibility: hidden">Search</label>
            <button
              type="button"
              class="btn button-default"
              @click="performSearch"
              :disabled="loading || loadingDealerTypes || loadingDealerGroups"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm"
              ></span>
              <span v-else>Search</span>
            </button>
          </div>

          <!-- Excel -->
          <div class="filter-item button-filter">
            <label style="visibility: hidden">Excel</label>
            <button
              type="button"
              class="btn button-export"
              @click="exportToExcel"
              :disabled="loading || !reportData.length"
            >
              Excel
            </button>
          </div>

          <!-- PDF -->
          <div class="filter-item button-filter">
            <label style="visibility: hidden">PDF</label>
            <button
              type="button"
              class="btn button-pdf"
              @click="exportToPdf"
              :disabled="loading || !reportData.length"
            >
              PDF
            </button>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="loading-spinner mt-3">
        <i class="fa fa-spinner fa-spin"></i> Loading...
      </div>

      <!-- Table -->
      <div v-if="!loading && reportData.length" class="report-card">
        <div class="table-shell">
          <table class="main-table">
            <thead>
              <tr class="report-title-row">
                <th colspan="8">Distributor - {{ selectedDistributorName }}</th>
              </tr>

              <tr class="header-row-1">
                <th>Dealer</th>
                <th>Item</th>
                <th class="text-end">Max QTY Allowed</th>
                <th class="text-end">Distributed QTY</th>
                <th class="text-end">Remaining QTY</th>
                <th>Credit Period</th>
                <th>Invoice Initiated Date</th>
                <th>Remaining Days for the Credit Period</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(row, index) in processedRows" :key="index">
                <td class="dealer-cell">
                  {{ row.showDealer ? row.dealer_name : "" }}
                </td>

                <td class="item-cell">
                  {{ row.item_name }}
                </td>

                <td class="text-end">
                  {{ formatQty(row.max_qty_allowed) }}
                </td>

                <td class="text-end">
                  {{ formatQty(row.distributed_qty) }}
                </td>

                <td class="text-end">
                  {{ formatQty(row.remaining_qty) }}
                </td>

                <td>
                  {{ row.credit_period || "-" }}
                </td>

                <td>
                  {{ formatDisplayDate(row.invoice_initiated_date) }}
                </td>

                <td class="text-end">
                  {{ row.remaining_credit_days }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Initial State -->
      <div v-if="!loading && !searchedOnce" class="idle-state">
        <div class="idle-icon">
          <i class="fa fa-file-text-o"></i>
        </div>

        <p>Select filters and click Search.</p>
      </div>

      <!-- Empty State -->
      <div
        v-if="!loading && searchedOnce && !reportData.length"
        class="no-data-message"
      >
        <p>No data found for selected filters.</p>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useToast } from "vue-toastification";
import axiosInstance, { API_BASE_URL } from "@/services/axiosInstance";

import {
  getDistributorList,
  getDealerGroups,
  getDealersByGroup,
  getDealerCategories,
  GetDealerListByDistributorIndividualDealerCategory,
} from "@/services/dataService";

const TRUST_PROCESS_REPORT_API_URL = `${API_BASE_URL}/web/trust-process-report`;

export default {
  name: "TrustProcessReportData",

  data() {
    const currentDate = new Date().toISOString().split("T")[0];

    const firstDayOfMonth = new Date();
    firstDayOfMonth.setDate(1);

    const fromDate = firstDayOfMonth.toISOString().split("T")[0];
    const ALL = "__all__";

    return {
      ALL,

      token: localStorage.getItem("token_set") || "",
      profileId: Number(localStorage.getItem("profile_id") || 0),
      myProfileId: Number(localStorage.getItem("profile_id") || 0),

      currentDate,
      fromDate,
      toDate: currentDate,

      loading: false,
      searchedOnce: false,
      redirecting: false,

      userLabel: "",
      roleKey: "OTHER",

      distributorList: [],
      selectedDistributorId: "",
      isDistributorLocked: false,

      selectedCategoryType: null,

      dealerGroupList: [],
      filteredDealerGroups: [],
      selectedDealerGroup: null,
      loadingDealerGroups: false,
      loadingDealerTypes: false,

      selectedBusinessType: "",
      dealerCategoryList: [],
      distDealerCache: [],

      dealerList: [],
      selectedDealerId: "",

      reportData: [],
    };
  },

  computed: {
    isDistributorUser() {
      return this.roleKey === "DIS";
    },

    isAsmUser() {
      return this.roleKey === "ASM";
    },

    isFsmUser() {
      return this.roleKey === "FSM";
    },

    isOtherUser() {
      return this.roleKey === "OTHER";
    },

    showDealerDropdown() {
      if (this.selectedCategoryType === "group") {
        return !!this.selectedDealerGroup?.group_id;
      }

      if (this.selectedCategoryType === "individual") {
        return !!this.selectedDistributorId && !!this.selectedBusinessType;
      }

      return false;
    },

    selectedDistributorName() {
      if (!this.selectedDistributorId) {
        return "-";
      }

      if (this.selectedDistributorId === this.ALL) {
        return "All Distributors";
      }

      const distributor = this.distributorList.find((item) => {
        return String(item.profile_id) === String(this.selectedDistributorId);
      });

      return (
        distributor?.business_name ||
        distributor?.business_display_name ||
        distributor?.name ||
        `Distributor #${this.selectedDistributorId}`
      );
    },

    selectedDealerCategoryLabel() {
      if (this.selectedCategoryType === "group") {
        return "Group";
      }

      if (this.selectedCategoryType === "individual") {
        return "Individual";
      }

      return "-";
    },

    selectedDealerTypeLabel() {
      if (this.selectedCategoryType !== "individual") {
        return "-";
      }

      if (!this.selectedBusinessType) {
        return "-";
      }

      const selected = this.dealerCategoryList.find((category) => {
        return String(category.id) === String(this.selectedBusinessType);
      });

      return selected?.dealer_cat_name || "-";
    },

    selectedDealerGroupLabel() {
      if (this.selectedCategoryType !== "group") {
        return "-";
      }

      return this.selectedDealerGroup?.group_name || "-";
    },

    selectedDealerName() {
      if (!this.selectedDealerId) {
        return "-";
      }

      if (this.selectedDealerId === this.ALL) {
        return "All Dealers";
      }

      const dealer = this.dealerList.find((item) => {
        return String(item.profile_id) === String(this.selectedDealerId);
      });

      return (
        dealer?.business_display_name ||
        dealer?.business_name ||
        dealer?.name ||
        `Dealer #${this.selectedDealerId}`
      );
    },

    processedRows() {
      let lastDealerKey = null;

      return this.reportData.map((row) => {
        const dealerKey = String(row.dealer_id || row.dealer_name || "");
        const showDealer = dealerKey !== lastDealerKey;

        lastDealerKey = dealerKey;

        return {
          ...row,
          showDealer,
        };
      });
    },
  },

  mounted() {
    this.init();
  },

  methods: {
    toast(type, message) {
      const toast = useToast();

      if (type === "error") {
        toast.error(message, {
          position: "top-right",
          timeout: 3000,
        });
      } else {
        toast.success(message, {
          position: "top-right",
          timeout: 3000,
        });
      }
    },

    bodyOf(response) {
      return response?.data ?? response;
    },

    isSuccessResponse(body) {
      const status = body?.status;

      return (
        status === true ||
        status === 1 ||
        status === "1" ||
        String(status).toLowerCase() === "true" ||
        String(status).toLowerCase() === "success"
      );
    },

    listOf(body) {
      if (Array.isArray(body)) {
        return body;
      }

      if (Array.isArray(body?.data)) {
        return body.data;
      }

      if (Array.isArray(body?.data?.data)) {
        return body.data.data;
      }

      if (Array.isArray(body?.result)) {
        return body.result;
      }

      return [];
    },

    mapDealerRow(dealer) {
      const profileId = dealer?.profile_id ?? "";

      const name =
        dealer?.business_display_name ||
        dealer?.business_name ||
        dealer?.name ||
        dealer?.dealer_name ||
        dealer?.profile_name ||
        "";

      return {
        ...dealer,

        profile_id: String(profileId),

        business_display_name: name,
        business_name: dealer?.business_name || name,
        name,

        dealer_group_id: dealer?.dealer_group_id || dealer?.group_id || null,

        dealer_cat_id: String(dealer?.dealer_cat_id || ""),
        dealer_cat_name: dealer?.dealer_cat_name || "",
      };
    },

    msgOf(value) {
      const body = this.bodyOf(value?.response?.data ?? value);

      return String(
        body?.message ||
          body?.error ||
          value?.message ||
          value?.response?.data?.message ||
          "",
      );
    },

    isUnauthorized(value) {
      const httpStatus = value?.response?.status ?? value?.status;
      const message = this.msgOf(value).toLowerCase();

      return (
        httpStatus === 401 ||
        httpStatus === 419 ||
        httpStatus === 440 ||
        httpStatus === 498 ||
        /access token is invalid|unauthenticated|token.*expired|expired token|invalid token|session.*expired/.test(
          message,
        )
      );
    },

    handleUnauthorized(value) {
      if (!this.isUnauthorized(value)) {
        return false;
      }

      this.redirectToLogin();
      return true;
    },

    redirectToLogin() {
      if (this.redirecting) {
        return;
      }

      this.redirecting = true;
      this.toast("error", "Session expired. Please login again.");

      [
        "token_set",
        "profile_id",
        "user_label",
        "userLabel",
        "role",
        "first_name",
        "last_name",
        "user_name",
        "username",
      ].forEach((key) => localStorage.removeItem(key));

      if (this.$router) {
        this.$router.replace("/login");
      } else {
        window.location.replace("/login");
      }
    },

    initRoleFromStorage() {
      const label = String(
        localStorage.getItem("user_label") ||
          localStorage.getItem("userLabel") ||
          localStorage.getItem("role") ||
          "",
      );

      this.userLabel = label;

      const normalized = label.toUpperCase().trim();

      if (
        normalized === "DISTRIBUTOR" ||
        normalized.startsWith("DISTRIBUTOR") ||
        normalized === "DIS" ||
        normalized.startsWith("DIS_")
      ) {
        this.roleKey = "DIS";
      } else if (normalized === "ASM" || normalized.startsWith("ASM_")) {
        this.roleKey = "ASM";
      } else if (normalized === "FSM" || normalized.startsWith("FSM_")) {
        this.roleKey = "FSM";
      } else {
        this.roleKey = "OTHER";
      }
    },

    async init() {
      this.loading = true;

      try {
        this.token = localStorage.getItem("token_set") || "";

        if (!this.token) {
          this.redirectToLogin();
          return;
        }

        this.profileId = Number(localStorage.getItem("profile_id") || 0);
        this.myProfileId = this.profileId;

        this.initRoleFromStorage();

        await this.loadDistributors();
      } catch (error) {
        if (this.handleUnauthorized(error)) {
          return;
        }

        console.error("Trust process report init error:", error);
        this.toast("error", "Failed to initialize report filters.");
      } finally {
        this.loading = false;
      }
    },

    async loadDealerCategories() {
      this.loadingDealerTypes = true;

      try {
        const response = await getDealerCategories(this.token);

        if (this.handleUnauthorized(response)) {
          return;
        }

        const body = this.bodyOf(response);

        if (!this.isSuccessResponse(body)) {
          this.dealerCategoryList = [];
          this.toast("error", body?.message || "Failed to load dealer types.");
          return;
        }

        this.dealerCategoryList = this.listOf(body).map((category) => ({
          id: String(category.id),
          dealer_cat_name:
            category.dealer_cat_name ||
            category.name ||
            category.category_name ||
            `Category #${category.id}`,
        }));

        if (!this.dealerCategoryList.length) {
          this.toast("error", "No dealer types found.");
        }
      } catch (error) {
        if (this.handleUnauthorized(error)) {
          return;
        }

        console.error("Dealer type load error:", error);
        this.dealerCategoryList = [];
        this.toast("error", "Failed to load dealer types.");
      } finally {
        this.loadingDealerTypes = false;
      }
    },

    async loadDistributors() {
      try {
        const response = await getDistributorList(this.profileId, this.token);

        if (this.handleUnauthorized(response)) {
          return;
        }

        const body = this.bodyOf(response);

        this.distributorList = this.listOf(body);

        if (this.isDistributorUser && this.myProfileId) {
          this.selectedDistributorId = String(this.myProfileId);
          this.isDistributorLocked = true;
          await this.onDistributorChange();
        } else {
          this.selectedDistributorId = "";
          this.isDistributorLocked = false;
        }
      } catch (error) {
        if (this.handleUnauthorized(error)) {
          return;
        }

        console.error("Distributor load error:", error);
        this.toast("error", "Failed to load distributor list.");
      }
    },

    async onDistributorChange() {
      this.selectedCategoryType = null;

      this.selectedDealerGroup = null;
      this.dealerGroupList = [];
      this.filteredDealerGroups = [];

      this.selectedBusinessType = "";
      this.dealerCategoryList = [];

      this.selectedDealerId = "";
      this.dealerList = [];
      this.distDealerCache = [];

      this.reportData = [];
      this.searchedOnce = false;
    },

    async onCategoryTypeChange() {
      this.selectedDealerGroup = null;
      this.dealerGroupList = [];
      this.filteredDealerGroups = [];

      this.selectedBusinessType = "";
      this.dealerCategoryList = [];

      this.selectedDealerId = "";
      this.dealerList = [];
      this.distDealerCache = [];
      this.reportData = [];

      if (this.selectedCategoryType === "group") {
        await this.loadDealerGroupsForDistributor();
      }

      if (this.selectedCategoryType === "individual") {
        await this.loadDealerCategories();
      }
    },

    async loadDealerGroupsForDistributor() {
      this.loadingDealerGroups = true;

      try {
        const distributorId =
          this.selectedDistributorId === this.ALL
            ? this.profileId
            : this.selectedDistributorId;

        const response = await getDealerGroups(distributorId, this.token);

        if (this.handleUnauthorized(response)) {
          return;
        }

        const body = this.bodyOf(response);

        if (!this.isSuccessResponse(body)) {
          this.dealerGroupList = [];
          this.filteredDealerGroups = [];
          this.toast("error", body?.message || "Failed to load dealer groups.");
          return;
        }

        this.dealerGroupList = this.listOf(body).map((group) => ({
          group_id: group.group_id || group.id,
          group_name: group.group_name || group.name || "-",
          distributor_id: group.distributor_id,
          dealers_count: group.dealers_count,
        }));

        this.filteredDealerGroups = [...this.dealerGroupList];
      } catch (error) {
        if (this.handleUnauthorized(error)) {
          return;
        }

        console.error("Dealer group load error:", error);
        this.toast("error", "Failed to load dealer groups.");
      } finally {
        this.loadingDealerGroups = false;
      }
    },

    async onDealerGroupChange() {
      this.selectedDealerId = "";
      this.dealerList = [];
      this.reportData = [];

      await this.loadGroupBranchDealers();
    },

    async loadGroupBranchDealers() {
      if (!this.selectedDealerGroup?.group_id) {
        return;
      }

      try {
        const groupId = Number(this.selectedDealerGroup.group_id);

        const response = await getDealersByGroup(groupId, this.token);

        if (this.handleUnauthorized(response)) {
          return;
        }

        const body = this.bodyOf(response);
        const data = this.listOf(body);

        this.dealerList = data
          .map((dealer) => this.mapDealerRow(dealer))
          .filter((dealer) => dealer.profile_id);

        if (!this.dealerList.length) {
          this.toast("error", "No dealers found for this group.");
        }
      } catch (error) {
        if (this.handleUnauthorized(error)) {
          return;
        }

        console.error("Group dealer load error:", error);
        this.toast("error", "Failed to load dealers for selected group.");
      }
    },

    async onBusinessTypeChange() {
      this.selectedDealerId = "";
      this.dealerList = [];
      this.distDealerCache = [];
      this.reportData = [];

      if (!this.selectedBusinessType) {
        return;
      }

      await this.loadDealersUnderDistributor();

      if (!this.dealerList.length) {
        this.toast("error", "No dealers found for selected dealer type.");
      }
    },

    async loadDealersUnderDistributor() {
      if (!this.selectedDistributorId || !this.selectedBusinessType) {
        this.distDealerCache = [];
        this.dealerList = [];
        return;
      }

      try {
        const distributorId =
          this.selectedDistributorId === this.ALL
            ? ""
            : this.selectedDistributorId;

        const payload = {
          user_profile_id: this.profileId,
          distributor_profile_id: distributorId,
          dealer_cat_id: this.selectedBusinessType,
        };

        console.log(
          "Calling GetDealerListByDistributorIndividualDealerCategory:",
          payload,
        );

        const response =
          await GetDealerListByDistributorIndividualDealerCategory(
            payload,
            this.token,
          );

        if (this.handleUnauthorized(response)) {
          return;
        }

        const body = this.bodyOf(response);

        if (!this.isSuccessResponse(body)) {
          console.error("Individual dealer dropdown API failed:", body);
          this.distDealerCache = [];
          this.dealerList = [];
          this.toast("error", body?.message || "Failed to load dealer list.");
          return;
        }

        this.distDealerCache = this.listOf(body)
          .map((dealer) => this.mapDealerRow(dealer))
          .filter((dealer) => dealer.profile_id);

        this.dealerList = [...this.distDealerCache];

        if (!this.dealerList.length) {
          console.warn("No individual dealers found.");
        }
      } catch (error) {
        if (this.handleUnauthorized(error)) {
          return;
        }

        console.error("Individual dealer dropdown load error:", error);
        this.distDealerCache = [];
        this.dealerList = [];
        this.toast("error", "Failed to load dealer list.");
      }
    },

    validateFilters() {
      if (!this.selectedDistributorId) {
        this.toast("error", "Please select Distributor.");
        return false;
      }

      if (!this.validateFromDate()) {
        return false;
      }

      if (!this.validateToDate()) {
        return false;
      }

      if (this.toDate < this.fromDate) {
        this.toast("error", "Invalid date range selected.");
        return false;
      }

      if (!this.selectedCategoryType) {
        this.toast("error", "Please select Dealer Category.");
        return false;
      }

      if (
        this.selectedCategoryType === "group" &&
        !this.selectedDealerGroup?.group_id
      ) {
        this.toast("error", "Please select Dealer Group.");
        return false;
      }

      if (
        this.selectedCategoryType === "individual" &&
        !this.selectedBusinessType
      ) {
        this.toast("error", "Please select Dealer Type.");
        return false;
      }

      if (!this.selectedDealerId) {
        this.toast("error", "Please select Dealer.");
        return false;
      }

      return true;
    },

    isValidISODate(dateStr) {
      if (!dateStr || typeof dateStr !== "string" || dateStr.includes("/")) {
        return false;
      }

      const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);

      if (!match) {
        return false;
      }

      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);

      const date = new Date(year, month - 1, day);

      return (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
      );
    },

    validateFromDate() {
      if (!this.fromDate || !this.isValidISODate(this.fromDate)) {
        this.toast("error", "Please select valid From Date.");
        this.fromDate = "";
        return false;
      }

      if (this.fromDate > this.currentDate) {
        this.toast("error", "From Date cannot be in the future.");
        this.fromDate = "";
        return false;
      }

      return true;
    },

    validateToDate() {
      if (!this.toDate || !this.isValidISODate(this.toDate)) {
        this.toast("error", "Please select valid To Date.");
        this.toDate = "";
        return false;
      }

      if (this.toDate > this.currentDate) {
        this.toast("error", "To Date cannot be in the future.");
        this.toDate = "";
        return false;
      }

      return true;
    },

    handleDateChange() {
      if (this.fromDate && this.toDate && this.toDate < this.fromDate) {
        this.toast("error", "Invalid date range selected.");
        this.toDate = "";
      }
    },

    async performSearch() {
      if (!this.validateFilters()) {
        return;
      }

      this.loading = true;
      this.searchedOnce = true;
      this.reportData = [];

      try {
        const payload = {
          start_date: this.fromDate,
          end_date: this.toDate,

          distributor_id:
            this.selectedDistributorId === this.ALL
              ? ""
              : String(this.selectedDistributorId),

          dealer_category: this.selectedCategoryType,

          dealer_cat_id:
            this.selectedCategoryType === "individual"
              ? this.selectedBusinessType || ""
              : "",

          dealer_group_id:
            this.selectedCategoryType === "group"
              ? this.selectedDealerGroup?.group_id || ""
              : "",

          dealer_id:
            this.selectedDealerId === this.ALL
              ? ""
              : String(this.selectedDealerId),
        };

        const response = await axiosInstance.post(
          TRUST_PROCESS_REPORT_API_URL,
          payload,
          {
            headers: {
              Authorization: `Bearer ${this.token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (this.handleUnauthorized(response)) {
          return;
        }

        const body = this.bodyOf(response);

        if (!this.isSuccessResponse(body)) {
          this.toast("error", body?.message || "No data found.");
          this.reportData = [];
          return;
        }

        const data = this.listOf(body);

        this.reportData = this.normalizeReportData(data).sort((a, b) => {
          const dealerA = String(a.dealer_name || "").toLowerCase();
          const dealerB = String(b.dealer_name || "").toLowerCase();

          if (dealerA < dealerB) return -1;
          if (dealerA > dealerB) return 1;

          const dateA = String(a.invoice_initiated_date || "");
          const dateB = String(b.invoice_initiated_date || "");

          if (dateA < dateB) return -1;
          if (dateA > dateB) return 1;

          const itemA = String(a.item_name || "").toLowerCase();
          const itemB = String(b.item_name || "").toLowerCase();

          if (itemA < itemB) return -1;
          if (itemA > itemB) return 1;

          return 0;
        });

        if (!this.reportData.length) {
          this.toast("error", "No data found for selected filters.");
          return;
        }

        this.toast("success", "Report loaded successfully.");
      } catch (error) {
        if (this.handleUnauthorized(error)) {
          return;
        }

        console.error("Trust process report load error:", error);

        this.toast(
          "error",
          error?.response?.data?.message || "Error loading report.",
        );
      } finally {
        this.loading = false;
      }
    },

    normalizeReportData(invoices) {
      if (!Array.isArray(invoices)) return [];

      const rows = [];

      invoices.forEach((invoice) => {
        const invoiceItems = Array.isArray(invoice.invoice_item)
          ? invoice.invoice_item
          : [];

        invoiceItems.forEach((item, itemIndex) => {
          rows.push({
            invoice_no: invoice.invoice_no || "",
            invoice_date: invoice.invoice_date || "",

            dealer_id:
              invoice.dealer_id ||
              invoice.dealer_profile_id ||
              invoice.profile_id ||
              "",

            dealer_name: invoice.dealer_name || "-",

            item_name: item.item_name || "-",
            item_price: this.toNumber(item.item_price),

            max_qty_allowed: this.toNumber(item.trust_limit),
            distributed_qty: this.toNumber(item.invoiced_qty),
            remaining_qty: this.toNumber(item.balance),

            credit_period: item.credit_period || "-",
            credit_period_days: item.credit_period_days || "-",

            invoice_initiated_date: invoice.invoice_date || "",

            remaining_credit_days:
              item.remaining_days === null ||
              item.remaining_days === undefined ||
              item.remaining_days === ""
                ? 0
                : item.remaining_days,

            itemIndex,
          });
        });
      });

      return rows;
    },

    toNumber(value) {
      if (value === null || value === undefined || value === "") {
        return 0;
      }

      const numberValue = Number(String(value).replace(/,/g, "").trim());

      return Number.isFinite(numberValue) ? numberValue : 0;
    },

    formatQty(value) {
      const numberValue = Number(value);

      if (!Number.isFinite(numberValue)) {
        return "0";
      }

      return numberValue.toLocaleString("en-US", {
        maximumFractionDigits: 0,
      });
    },

    formatDisplayDate(value) {
      if (!value) {
        return "-";
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return date.toLocaleDateString("en-CA");
    },

    getGeneratedByName() {
      const firstName = localStorage.getItem("first_name") || "";
      const lastName = localStorage.getItem("last_name") || "";

      return (
        `${firstName} ${lastName}`.trim() ||
        localStorage.getItem("user_name") ||
        localStorage.getItem("username") ||
        "-"
      );
    },

    getGeneratedAt() {
      return new Date().toLocaleString("en-US", {
        hour12: true,
      });
    },

    getExportFileBaseName() {
      return `Trust_Process_Report_${this.fromDate}_to_${this.toDate}`;
    },

    getExportRows() {
      return this.processedRows.map((row) => ({
        Dealer: row.showDealer ? row.dealer_name : "",
        Item: row.item_name,
        "Max QTY Allowed": row.max_qty_allowed,
        "Distributed QTY": row.distributed_qty,
        "Remaining QTY": row.remaining_qty,
        "Credit Period": row.credit_period,
        "Invoice Initiated Date": this.formatDisplayDate(
          row.invoice_initiated_date,
        ),
        "Remaining Days for the Credit Period": row.remaining_credit_days,
      }));
    },

    exportToExcel() {
      if (!this.reportData.length) {
        this.toast("error", "No data to export.");
        return;
      }

      const generatedBy = this.getGeneratedByName();
      const generatedAt = this.getGeneratedAt();

      const aoa = [
        ["Trust (Loan) Process Report"],
        [],
        ["Start Date", this.fromDate],
        ["End Date", this.toDate],
        ["Distributor", this.selectedDistributorName],
        ["Dealer Category", this.selectedDealerCategoryLabel],
        ["Dealer Type", this.selectedDealerTypeLabel],
        ["Dealer Group", this.selectedDealerGroupLabel],
        ["Dealer", this.selectedDealerName],
        [],
        [
          "Dealer",
          "Item",
          "Max QTY Allowed",
          "Distributed QTY",
          "Remaining QTY",
          "Credit Period",
          "Invoice Initiated Date",
          "Remaining Days for the Credit Period",
        ],
        ...this.getExportRows().map((row) => [
          row.Dealer,
          row.Item,
          row["Max QTY Allowed"],
          row["Distributed QTY"],
          row["Remaining QTY"],
          row["Credit Period"],
          row["Invoice Initiated Date"],
          row["Remaining Days for the Credit Period"],
        ]),
        [],
        ["Generated By", generatedBy],
        ["Generated Date", generatedAt],
      ];

      const ws = XLSX.utils.aoa_to_sheet(aoa);

      ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 7 } }];

      ws["!cols"] = [
        { wch: 28 },
        { wch: 22 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 24 },
        { wch: 22 },
        { wch: 32 },
      ];

      const wb = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(wb, ws, "Trust Process Report");
      XLSX.writeFile(wb, `${this.getExportFileBaseName()}.xlsx`);

      this.toast("success", "Excel exported successfully.");
    },

    exportToPdf() {
      if (!this.reportData.length) {
        this.toast("error", "No data to export.");
        return;
      }

      const generatedBy = this.getGeneratedByName();
      const generatedAt = this.getGeneratedAt();

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "a4",
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      doc.setFontSize(13);
      doc.setFont(undefined, "bold");

      doc.text("Trust (Loan) Process Report", pageWidth / 2, 30, {
        align: "center",
      });

      doc.setFontSize(8);
      doc.setFont(undefined, "normal");

      const filterText = [
        `From: ${this.fromDate}`,
        `To: ${this.toDate}`,
        `Distributor: ${this.selectedDistributorName}`,
        `Category: ${this.selectedDealerCategoryLabel}`,
        `Dealer Type: ${this.selectedDealerTypeLabel}`,
        `Group: ${this.selectedDealerGroupLabel}`,
        `Dealer: ${this.selectedDealerName}`,
      ].join("   |   ");

      doc.text(filterText, 24, 50, {
        maxWidth: pageWidth - 48,
      });

      const body = this.processedRows.map((row) => [
        row.showDealer ? row.dealer_name : "",
        row.item_name,
        this.formatQty(row.max_qty_allowed),
        this.formatQty(row.distributed_qty),
        this.formatQty(row.remaining_qty),
        row.credit_period || "-",
        this.formatDisplayDate(row.invoice_initiated_date),
        row.remaining_credit_days,
      ]);

      autoTable(doc, {
        startY: 72,
        head: [
          [
            "Dealer",
            "Item",
            "Max QTY Allowed",
            "Distributed QTY",
            "Remaining QTY",
            "Credit Period",
            "Invoice Date",
            "Remaining Days",
          ],
        ],
        body,
        styles: {
          fontSize: 7,
          cellPadding: 3,
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [90, 97, 102],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: 95 },
          1: { cellWidth: 85 },
          5: { cellWidth: 105 },
          7: { cellWidth: 85 },
        },
        margin: {
          top: 72,
          right: 24,
          bottom: 42,
          left: 24,
        },
        didDrawPage: () => {
          doc.setFontSize(8);
          doc.setFont(undefined, "normal");

          doc.text(`Generated By: ${generatedBy}`, 24, pageHeight - 24);

          doc.text(
            `Generated Date: ${generatedAt}`,
            pageWidth / 2,
            pageHeight - 24,
            {
              align: "center",
            },
          );

          doc.text(
            `Page ${doc.internal.getNumberOfPages()}`,
            pageWidth - 24,
            pageHeight - 24,
            {
              align: "right",
            },
          );
        },
      });

      doc.save(`${this.getExportFileBaseName()}.pdf`);

      this.toast("success", "PDF exported successfully.");
    },
  },
};
</script>

<style scoped>
html,
body {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0;
}

.trust-report-page {
  padding-bottom: 20px;
}

.trust-report-container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.08);
  border-radius: 7px;
  padding: 14px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  width: calc(100% - 20px);
  box-sizing: border-box;
}

.filter-panel {
  border: 1px solid #e7e7e7;
  border-radius: 6px;
  background: #ffffff;
  padding: 10px;
  margin-bottom: 12px;
}

.filter-container {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 8px;
  width: 100%;
  align-items: end;
}

.distributor-filter {
  grid-column: span 3;
}

.date-filter {
  grid-column: span 2;
}

.category-filter {
  grid-column: span 2;
}

.group-filter,
.type-filter {
  grid-column: span 3;
}

.dealer-filter {
  grid-column: span 4;
}

.button-filter {
  grid-column: span 1;
  min-width: 70px;
}

.filter-item {
  min-width: 0;
  width: 100%;
}

.filter-item label {
  display: block;
  margin-bottom: 4px;
  font-weight: 700;
  font-size: 11.5px;
  color: #333333;
  line-height: 1.2;
}

.filter-item input,
.filter-item select,
.filter-item button {
  width: 100% !important;
  height: 32px !important;
  min-height: 32px !important;
  box-sizing: border-box;
}

.filter-item input,
.filter-item select {
  padding: 5px 8px;
  border: 1px solid #cccccc;
  border-radius: 4px;
  background: #ffffff;
  color: #333333;
  font-size: 12px;
  line-height: 1.2;
}

.filter-item input:focus,
.filter-item select:focus {
  border-color: #007bff;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
}

.filter-item select:disabled,
.filter-item input:disabled {
  background-color: #f4f4f4;
  color: #777777;
  cursor: not-allowed;
}

.btn.button-default,
.btn.button-export,
.btn.button-pdf {
  height: 32px !important;
  min-height: 32px !important;
  padding: 5px 10px;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  transition:
    background-color 0.15s ease,
    opacity 0.15s ease,
    transform 0.15s ease;
}

.btn.button-default {
  background-color: #007bff;
}

.btn.button-default:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn.button-export {
  background-color: #28a745;
}

.btn.button-export:hover:not(:disabled) {
  background-color: #218838;
}

.btn.button-pdf {
  background-color: #dc3545;
}

.btn.button-pdf:hover:not(:disabled) {
  background-color: #c82333;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  opacity: 0.94;
}

.btn:disabled {
  background-color: #6c757d !important;
  cursor: not-allowed;
  opacity: 0.75;
  box-shadow: none;
  transform: none;
}

.loading-spinner {
  font-size: 16px;
  text-align: center;
  margin-top: 16px;
  color: #007bff;
}

.report-card {
  width: 100%;
  background: #ffffff;
  border-radius: 6px;
}

.table-shell {
  width: 100%;
  max-height: 72dvh;
  overflow: auto;
  border: 1px solid #dddddd;
  border-radius: 5px;
}

.main-table {
  width: 100%;
  min-width: 1120px;
  border-collapse: separate;
  border-spacing: 0;
  margin: 0;
}

.main-table th,
.main-table td {
  border-right: 1px solid #dddddd;
  border-bottom: 1px solid #dddddd;
  padding: 7px 8px;
  white-space: nowrap;
  font-size: 12px;
  color: #222222;
  background: #ffffff;
}

.main-table thead th {
  position: sticky;
  z-index: 3;
  background: #d9d9d9;
  color: #111111;
  font-weight: 800;
}

.report-title-row th {
  top: 0;
  text-align: left;
  background: #ffffff !important;
  border-top: 1px solid #dddddd;
}

.header-row-1 th {
  top: 31px;
}

.text-center {
  text-align: center;
}

.text-end {
  text-align: right;
}

.group-end {
  border-right: 2px solid #c8c8c8 !important;
}

.dealer-cell {
  font-weight: 700;
}

.item-cell {
  font-weight: 600;
}

.main-table tbody tr:hover td {
  background: #fafafa;
}

.no-data-message,
.idle-state {
  width: 100%;
  text-align: center;
  padding: 24px 10px;
}

.no-data-message p,
.idle-state p {
  color: #777777;
  font-size: 13px;
  margin: 0;
}

.idle-icon {
  width: 40px;
  height: 40px;
  border: 1px solid #dddddd;
  border-radius: 50%;
  background: #ffffff;
  color: #6c757d;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.trust-report-page,
.trust-report-page * {
  scrollbar-width: thin;
  scrollbar-color: rgba(90, 97, 102, 0.34) transparent;
}

.trust-report-page *::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.trust-report-page *::-webkit-scrollbar-track {
  background: transparent;
}

.trust-report-page *::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(90, 97, 102, 0.28);
}

.trust-report-page *::-webkit-scrollbar-thumb:hover {
  background: rgba(90, 97, 102, 0.55);
}

.trust-report-page *::-webkit-scrollbar-corner {
  background: transparent;
}

@media (max-width: 1300px) {
  .filter-container {
    grid-template-columns: repeat(8, minmax(0, 1fr));
  }

  .distributor-filter {
    grid-column: span 3;
  }

  .date-filter {
    grid-column: span 2;
  }

  .category-filter {
    grid-column: span 2;
  }

  .group-filter,
  .type-filter {
    grid-column: span 3;
  }

  .dealer-filter {
    grid-column: span 4;
  }

  .button-filter {
    grid-column: span 1;
  }
}

@media (max-width: 1000px) {
  .filter-container {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  .distributor-filter {
    grid-column: span 3;
  }

  .date-filter {
    grid-column: span 3;
  }

  .category-filter {
    grid-column: span 3;
  }

  .group-filter,
  .type-filter,
  .dealer-filter {
    grid-column: span 6;
  }

  .button-filter {
    grid-column: span 2;
  }
}

@media (max-width: 700px) {
  .trust-report-container {
    padding: 12px;
    margin: 6px;
    width: calc(100% - 12px);
    border-radius: 6px;
  }

  .filter-panel {
    padding: 9px;
  }

  .filter-container {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .distributor-filter,
  .group-filter,
  .type-filter,
  .dealer-filter {
    grid-column: span 2;
  }

  .date-filter,
  .category-filter,
  .button-filter {
    grid-column: span 1;
  }

  .result-toolbar {
    padding: 8px;
  }
}

@media (max-width: 520px) {
  .filter-container {
    grid-template-columns: 1fr;
  }

  .distributor-filter,
  .date-filter,
  .category-filter,
  .group-filter,
  .type-filter,
  .dealer-filter,
  .button-filter {
    grid-column: span 1;
  }

  .filter-item input,
  .filter-item select,
  .filter-item button {
    height: 34px !important;
    min-height: 34px !important;
  }

  .table-shell {
    max-height: none;
  }

  .main-table {
    min-width: 980px;
  }
}
</style>